import { PiMeasure, PiCategory } from '@models/mips-stepper.model';

export const PI_MEASURES_2025: PiMeasure[] = [

  // ============================================
  // E-PRESCRIBING
  // ============================================
  {
    measureId: 'PI_EP_1',
    measureTitle: 'e-Prescribing',
    category: 'e_prescribing',
    categoryDisplayName: 'e-Prescribing',
    valueType: 'numerator_denominator',
    maxPoints: 10,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_LVPP_1',
        description: 'Any MIPS eligible clinician who writes fewer than 100 permissible prescriptions during the performance period.',
        canClaim: false,
      },
    ],
    description: 'At least one permissible prescription written by the MIPS eligible clinician is transmitted electronically using CEHRT.',
    pointRedistribution: {
      targetMeasureIds: ['PI_HIE_1', 'PI_HIE_4'],
      pointsEach: 5,
    },
  },
  {
    measureId: 'PI_EP_2',
    measureTitle: 'Query of Prescription Drug Monitoring Program (PDMP)',
    category: 'e_prescribing',
    categoryDisplayName: 'e-Prescribing',
    valueType: 'yes_no',
    maxPoints: 10,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_EP_2_EX_1',
        description: 'Is unable to electronically prescribe Schedule II opioids and Schedule III and IV drugs in accordance with applicable law during the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_EP_2_EX_2',
        description: 'Does not electronically prescribe any Schedule II opioids or Schedule III or IV drugs during the performance period.',
        canClaim: false,
      },
    ],
    description: 'For at least one Schedule II opioid or Schedule III or IV drug electronically prescribed, the clinician uses CEHRT to conduct a query of a PDMP.',
    pointRedistribution: {
      targetMeasureId: 'PI_EP_1',
      points: 10,
    },
  },
  
  // ============================================
  // HEALTH INFORMATION EXCHANGE
  // ============================================
  {
    measureId: 'PI_HIE_1',
    measureTitle: 'Support Electronic Referral Loops by Sending Health Information',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'numerator_denominator',
    maxPoints: 15,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_LVOTC_1',
        description: 'Any MIPS eligible clinician who transfers a patient to another setting or refers a patient fewer than 100 times during the performance period.',
        canClaim: false,
      },
    ],
    description: 'For at least one transition of care or referral, the clinician creates a summary of care record using CEHRT and electronically exchanges the summary of care record.',
    pointRedistribution: {
      targetMeasureId: 'PI_PEA_1',
      points: 15,
    },
  },
  {
    measureId: 'PI_HIE_4',
    measureTitle: 'Support Electronic Referral Loops by Receiving and Reconciling Health Information',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'numerator_denominator',
    maxPoints: 15,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_LVITC_2',
        description: 'Any MIPS eligible clinician who receives transitions of care or referrals or has patient encounters in which the MIPS eligible clinician has never before encountered the patient fewer than 100 times during the performance period.',
        canClaim: false,
      },
    ],
    replacedMeasureIds: ['PI_HIE_5', 'PI_HIE_6'],
    description: 'For at least one electronic summary of care record received, the clinician conducts clinical information reconciliation for medication, medication allergy, and current problem list.',
    pointRedistribution: {
      targetMeasureId: 'PI_HIE_1',
      points: 15,
    },
  },
  {
    measureId: 'PI_HIE_5',
    measureTitle: 'Health Information Exchange (HIE) Bi-Directional Exchange',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'yes_no',
    maxPoints: 30,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: true,
    replacedMeasureIds: ['PI_HIE_1', 'PI_HIE_4'],
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    attestationStatements: [
      {
        id: 'hie_5_statement_1',
        question: 'I participate in an HIE in order to enable secure, bi-directional exchange to occur for every patient encounter, transition or referral, and record stored or maintained in the EHR during the performance period in accordance with applicable law and policy.',
        answer: '',
        required: true,
      },
      {
        id: 'hie_5_statement_2',
        question: 'The HIE that I participate in is capable of exchanging information across a broad network of unaffiliated exchange partners including those using disparate EHRs, and does not engage in exclusionary behavior when determining exchange partners.',
        answer: '',
        required: true,
      },
      {
        id: 'hie_5_statement_3',
        question: 'I use the functions of CEHRT to support bi-directional exchange with an HIE.',
        answer: '',
        required: true,
      },
    ],
    description: 'The MIPS eligible clinician or group must attest that they engage in bi-directional exchange with an HIE to support transitions of care.',
  },
  {
    measureId: 'PI_HIE_6',
    measureTitle: 'Enabling Exchange Under the Trusted Exchange Framework and Common Agreement (TEFCA)',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'yes_no',
    maxPoints: 30,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: true,
    replacedMeasureIds: ['PI_HIE_1', 'PI_HIE_4'],
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    attestationStatements: [
      {
        id: 'hie_6_statement_1',
        question: 'Participating as a signatory to a Framework Agreement (as defined by the Common Agreement for Nationwide Health Information Interoperability) in good standing and enabling secure, bi-directional exchange of information to occur for every patient encounter, transition or referral, and record stored or maintained in the EHR during the performance period, in accordance with applicable law and policy.',
        answer: '',
        required: true,
      },
      {
        id: 'hie_6_statement_2',
        question: 'Using the functions of CEHRT to support bi-directional exchange of patient information, in production, under this Framework Agreement.',
        answer: '',
        required: true,
      },
    ],
    description: 'The MIPS eligible clinician must attest to participating in TEFCA framework and using CEHRT to support bi-directional exchange.',
  },
  
  // ============================================
  // PROVIDER TO PATIENT EXCHANGE
  // ============================================
  {
    measureId: 'PI_PEA_1',
    measureTitle: 'Provide Patients Electronic Access to Their Health Information',
    category: 'provider_to_patient_exchange',
    categoryDisplayName: 'Provider to Patient Exchange',
    valueType: 'numerator_denominator',
    maxPoints: 25,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [],
    description: 'For at least one unique patient, the patient is provided timely access to view online, download, and transmit their health information, and access via API.',
  },
  
  // ============================================
  // PUBLIC HEALTH AND CLINICAL DATA EXCHANGE
  // ============================================
  {
    measureId: 'PI_PHCDRR_1',
    measureTitle: 'Immunization Registry Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: 25,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_1_PRE',
      validatedProduction: 'PI_PHCDRR_1_PROD',
    },
    sharedPointsWith: ['PI_PHCDRR_3'],
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_PHCDRR_1_EX_1',
        description: 'Does not administer any immunizations to any of the populations for which data is collected by the jurisdiction\'s immunization registry during the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_1_EX_2',
        description: 'Operates in a jurisdiction where no registry is capable of accepting the specific standards required by CEHRT at the start of the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_1_EX_3',
        description: 'Operates in a jurisdiction where no registry has declared readiness to receive immunization data as of 6 months prior to the start of the performance period.',
        canClaim: false,
      },
    ],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to submit immunization data and receive immunization forecasts and histories.',
  },
  {
    measureId: 'PI_PHCDRR_2',
    measureTitle: 'Syndromic Surveillance Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: null,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: true,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_2_PRE',
      validatedProduction: 'PI_PHCDRR_2_PROD',
    },
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to submit syndromic surveillance data from an urgent care setting. (Optional - 5 bonus points)',
  },
  {
    measureId: 'PI_PHCDRR_3',
    measureTitle: 'Electronic Case Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: 25,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_3_PRE',
      validatedProduction: 'PI_PHCDRR_3_PROD',
    },
    sharedPointsWith: ['PI_PHCDRR_1'],
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_PHCDRR_3_EX_1',
        description: 'Does not treat or diagnose any reportable diseases for which data is collected by the jurisdiction\'s reportable disease system during the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_3_EX_2',
        description: 'Operates in a jurisdiction where no public health agency is capable of receiving eCR data in the specific standards required at the start of the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_3_EX_3',
        description: 'Operates in a jurisdiction where no public health agency has declared readiness to receive eCR data as of 6 months prior to the start of the performance period.',
        canClaim: false,
      },
    ],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to electronically submit case reporting of reportable conditions.',
  },
  {
    measureId: 'PI_PHCDRR_4',
    measureTitle: 'Public Health Registry Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: null,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: true,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_4_PRE',
      validatedProduction: 'PI_PHCDRR_4_PROD',
    },
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to submit data to public health registries. (Optional - 5 bonus points)',
  },
  {
    measureId: 'PI_PHCDRR_5',
    measureTitle: 'Clinical Data Registry Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: null,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: true,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_5_PRE',
      validatedProduction: 'PI_PHCDRR_5_PROD',
    },
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    description: 'The MIPS eligible clinician is in active engagement to submit data to a clinical data registry. (Optional - 5 bonus points)',
  },
];

export const PI_MEASURES_2026: PiMeasure[] = [

  // ============================================
  // E-PRESCRIBING
  // ============================================
  {
    measureId: 'PI_EP_1',
    measureTitle: 'e-Prescribing',
    category: 'e_prescribing',
    categoryDisplayName: 'e-Prescribing',
    valueType: 'numerator_denominator',
    maxPoints: 10,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_LVPP_1',
        description: 'Any MIPS eligible clinician who writes fewer than 100 permissible prescriptions during the performance period.',
        canClaim: false,
      },
    ],
    description: 'At least one permissible prescription written by the MIPS eligible clinician is transmitted electronically using CEHRT.',
    pointRedistribution: {
      targetMeasureIds: ['PI_HIE_1', 'PI_HIE_4'],
      pointsEach: 5,
    },
  },
  {
    measureId: 'PI_EP_2',
    measureTitle: 'Query of Prescription Drug Monitoring Program (PDMP)',
    category: 'e_prescribing',
    categoryDisplayName: 'e-Prescribing',
    valueType: 'yes_no',
    maxPoints: 10,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_EP_2_EX_1',
        description: 'Is unable to electronically prescribe Schedule II opioids and Schedule III and IV drugs in accordance with applicable law during the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_EP_2_EX_2',
        description: 'Does not electronically prescribe any Schedule II opioids or Schedule III or IV drugs during the performance period.',
        canClaim: false,
      },
    ],
    description: 'For at least one Schedule II opioid or Schedule III or IV drug electronically prescribed, the clinician uses CEHRT to conduct a query of a PDMP.',
    pointRedistribution: {
      targetMeasureId: 'PI_EP_1',
      points: 10,
    },
  },

  // ============================================
  // HEALTH INFORMATION EXCHANGE
  // ============================================
  {
    measureId: 'PI_HIE_1',
    measureTitle: 'Support Electronic Referral Loops by Sending Health Information',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'numerator_denominator',
    maxPoints: 15,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_LVOTC_1',
        description: 'Any MIPS eligible clinician who transfers a patient to another setting or refers a patient fewer than 100 times during the performance period.',
        canClaim: false,
      },
    ],
    description: 'For at least one transition of care or referral, the clinician creates a summary of care record using CEHRT and electronically exchanges the summary of care record.',
    pointRedistribution: {
      targetMeasureId: 'PI_PEA_1',
      points: 15,
    },
  },
  {
    measureId: 'PI_HIE_4',
    measureTitle: 'Support Electronic Referral Loops by Receiving and Reconciling Health Information',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'numerator_denominator',
    maxPoints: 15,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_LVITC_2',
        description: 'Any MIPS eligible clinician who receives transitions of care or referrals or has patient encounters in which the MIPS eligible clinician has never before encountered the patient fewer than 100 times during the performance period.',
        canClaim: false,
      },
    ],
    replacedMeasureIds: ['PI_HIE_5', 'PI_HIE_6'],
    description: 'For at least one electronic summary of care record received, the clinician conducts clinical information reconciliation for medication, medication allergy, and current problem list.',
    pointRedistribution: {
      targetMeasureId: 'PI_HIE_1',
      points: 15,
    },
  },
  {
    measureId: 'PI_HIE_5',
    measureTitle: 'Health Information Exchange (HIE) Bi-Directional Exchange',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'yes_no',
    maxPoints: 30,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: true,
    replacedMeasureIds: ['PI_HIE_1', 'PI_HIE_4'],
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    attestationStatements: [
      {
        id: 'hie_5_statement_1',
        question: 'I participate in an HIE in order to enable secure, bi-directional exchange to occur for every patient encounter, transition or referral, and record stored or maintained in the EHR during the performance period in accordance with applicable law and policy.',
        answer: '',
        required: true,
      },
      {
        id: 'hie_5_statement_2',
        question: 'The HIE that I participate in is capable of exchanging information across a broad network of unaffiliated exchange partners including those using disparate EHRs, and does not engage in exclusionary behavior when determining exchange partners.',
        answer: '',
        required: true,
      },
      {
        id: 'hie_5_statement_3',
        question: 'I use the functions of CEHRT to support bi-directional exchange with an HIE.',
        answer: '',
        required: true,
      },
    ],
    description: 'The MIPS eligible clinician or group must attest that they engage in bi-directional exchange with an HIE to support transitions of care.',
  },
  {
    measureId: 'PI_HIE_6',
    measureTitle: 'Enabling Exchange Under the Trusted Exchange Framework and Common Agreement (TEFCA)',
    category: 'health_information_exchange',
    categoryDisplayName: 'Health Information Exchange',
    valueType: 'yes_no',
    maxPoints: 30,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: true,
    replacedMeasureIds: ['PI_HIE_1', 'PI_HIE_4'],
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    attestationStatements: [
      {
        id: 'hie_6_statement_1',
        question: 'Participating as a signatory to a Framework Agreement (as defined by the Common Agreement for Nationwide Health Information Interoperability) in good standing and enabling secure, bi-directional exchange of information to occur for every patient encounter, transition or referral, and record stored or maintained in the EHR during the performance period, in accordance with applicable law and policy.',
        answer: '',
        required: true,
      },
      {
        id: 'hie_6_statement_2',
        question: 'Using the functions of CEHRT to support bi-directional exchange of patient information, in production, under this Framework Agreement.',
        answer: '',
        required: true,
      },
    ],
    description: 'The MIPS eligible clinician must attest to participating in TEFCA framework and using CEHRT to support bi-directional exchange.',
  },

  // ============================================
  // PROVIDER TO PATIENT EXCHANGE
  // ============================================
  {
    measureId: 'PI_PEA_1',
    measureTitle: 'Provide Patients Electronic Access to Their Health Information',
    category: 'provider_to_patient_exchange',
    categoryDisplayName: 'Provider to Patient Exchange',
    valueType: 'numerator_denominator',
    maxPoints: 25,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    numerator: 0,
    denominator: 0,
    performance: 0,
    points: 0,
    selected: true,
    completed: false,
    exclusions: [],
    description: 'For at least one unique patient, the patient is provided timely access to view online, download, and transmit their health information, and access via API.',
  },

  // ============================================
  // PUBLIC HEALTH AND CLINICAL DATA EXCHANGE
  // ============================================
  {
    measureId: 'PI_PHCDRR_1',
    measureTitle: 'Immunization Registry Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: 25,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_1_PRE',
      validatedProduction: 'PI_PHCDRR_1_PROD',
    },
    sharedPointsWith: ['PI_PHCDRR_3'],
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_PHCDRR_1_EX_1',
        description: 'Does not administer any immunizations to any of the populations for which data is collected by the jurisdiction\'s immunization registry during the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_1_EX_2',
        description: 'Operates in a jurisdiction where no registry is capable of accepting the specific standards required by CEHRT at the start of the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_1_EX_3',
        description: 'Operates in a jurisdiction where no registry has declared readiness to receive immunization data as of 6 months prior to the start of the performance period.',
        canClaim: false,
      },
    ],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to submit immunization data and receive immunization forecasts and histories.',
  },
  {
    measureId: 'PI_PHCDRR_2',
    measureTitle: 'Syndromic Surveillance Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: null,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: true,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_2_PRE',
      validatedProduction: 'PI_PHCDRR_2_PROD',
    },
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to submit syndromic surveillance data from an urgent care setting. (Optional - 5 bonus points)',
  },
  {
    measureId: 'PI_PHCDRR_3',
    measureTitle: 'Electronic Case Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: 25,
    isRequired: true,
    isBlocking: false,
    isBonusMeasure: false,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_3_PRE',
      validatedProduction: 'PI_PHCDRR_3_PROD',
    },
    sharedPointsWith: ['PI_PHCDRR_1'],
    points: 0,
    selected: true,
    completed: false,
    exclusions: [
      {
        exclusionId: 'PI_PHCDRR_3_EX_1',
        description: 'Does not treat or diagnose any reportable diseases for which data is collected by the jurisdiction\'s reportable disease system during the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_3_EX_2',
        description: 'Operates in a jurisdiction where no public health agency is capable of receiving eCR data in the specific standards required at the start of the performance period.',
        canClaim: false,
      },
      {
        exclusionId: 'PI_PHCDRR_3_EX_3',
        description: 'Operates in a jurisdiction where no public health agency has declared readiness to receive eCR data as of 6 months prior to the start of the performance period.',
        canClaim: false,
      },
    ],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to electronically submit case reporting of reportable conditions.',
  },
  {
    measureId: 'PI_PHCDRR_4',
    measureTitle: 'Public Health Registry Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: null,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: true,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_4_PRE',
      validatedProduction: 'PI_PHCDRR_4_PROD',
    },
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    description: 'The MIPS eligible clinician is in active engagement with a public health agency to submit data to public health registries. (Optional - 5 bonus points)',
  },
  {
    measureId: 'PI_PHCDRR_5',
    measureTitle: 'Clinical Data Registry Reporting',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no_engagement',
    maxPoints: null,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: true,
    isAlternativeMeasure: false,
    requiresEngagementLevel: true,
    activeEngagementLevelIds: {
      preProduction: 'PI_PHCDRR_5_PRE',
      validatedProduction: 'PI_PHCDRR_5_PROD',
    },
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    description: 'The MIPS eligible clinician is in active engagement to submit data to a clinical data registry. (Optional - 5 bonus points)',
  },
  {
    measureId: 'PI_PHCDRR_6',
    measureTitle: 'Public Health Reporting Using the Trusted Exchange Framework and Common Agreement™ (TEFCA™)',
    category: 'public_health_clinical_data_exchange',
    categoryDisplayName: 'Public Health and Clinical Data Exchange',
    valueType: 'yes_no',
    maxPoints: null,
    isRequired: false,
    isBlocking: false,
    isBonusMeasure: true,
    isAlternativeMeasure: false,
    points: 0,
    selected: false,
    completed: false,
    exclusions: [],
    description: 'The MIPS eligible clinician is in active engagement to submit public health data using the Trusted Exchange Framework and Common Agreement™ (TEFCA™). (Optional - 5 bonus points)',
  },
];

export const PI_CATEGORY_ORDER: PiCategory[] = [
  'attestations',
  'e_prescribing',
  'health_information_exchange',
  'provider_to_patient_exchange',
  'public_health_clinical_data_exchange',
];

export const PI_CATEGORY_DISPLAY_NAMES: Record<PiCategory, string> = {
  attestations: 'Required Attestations',
  e_prescribing: 'e-Prescribing',
  health_information_exchange: 'Health Information Exchange',
  provider_to_patient_exchange: 'Provider to Patient Exchange',
  public_health_clinical_data_exchange: 'Public Health and Clinical Data Exchange',
};
